import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { INSIGHTS_FIELD, isModelNode, isReadOnlyModelType, LAYOUTS, Linqset, MODEL_TYPE, NODE_TYPE } from '../../common/linqset-model';
import { ModelDetails, Organisation, Tag, TagGroup } from '../../common/organisation';
import { TagsService } from '../../organisation/tags/tags-service';
import { LAYOUT_DIRECTION, USER_ROLE } from '../../common/user';
import { FUNCTION, FunctionService, Function } from '../../model-services/function.service';
import { EXPORT_TYPE } from '../../model-services/download.service';
import { LinqModelToCanvasService } from '../../model-services/linqmodel.to.canvas.service';
import { SCREEN } from '../../layouts/layouts-enums';
import { AppComponent } from '../../app.component';
import { GROUP_TYPE } from '../../model-services/group.service';
import { GraphComponent } from '@yfiles/yfiles';
import { ModelIntroductionService } from '../../animations/model-introduction';
import { LabelService } from '../../model-services/label.service';

// list of functions excluded from organisation model
const ORG_EXCLUDED_FUNCTIONS = [FUNCTION.BACKUP_MODEL, FUNCTION.RESTORE_BACKUP, FUNCTION.APPLY_FILTER, FUNCTION.CLEAR_FILTER, FUNCTION.MOVE_SELECTION_TO_MODEL, FUNCTION.OPEN_MODELLING_AI, FUNCTION.TOGGLE_TEMPORAL_MODEL]

// on the toolbar itself from a medium screen up; the rest are in its menu. Building with AI is only offered for an empty
// model, where it is the main thing to do, so it is shown rather than kept in the menu
const MEDIUM_FUNCTIONS = [FUNCTION.SAVE_MODEL, FUNCTION.FIT_TO_SCREEN, FUNCTION.DOWNLOAD_MODEL, FUNCTION.RESET_LAYOUT, FUNCTION.INCREMENTAL_LAYOUT, FUNCTION.SWITCH_TO_PAN_MODE, FUNCTION.SWITCH_TO_SELECT_MODE,FUNCTION.CHANGE_MODEL_TYPE, FUNCTION.CREATE_GROUPS, FUNCTION.REMOVE_GROUPS, FUNCTION.MANAGE_LABELS, FUNCTION.OPEN_MODELLING_AI]
const LARGE_FUNCTIONS = [FUNCTION.TOGGLE_HEATMAP, FUNCTION.TOGGLE_MAGNIFYING_GLASS, FUNCTION.ZOOM_IN, FUNCTION.ZOOM_OUT, FUNCTION.EXPAND_ALL, FUNCTION.COLLAPSE_ALL, FUNCTION.SELECT_TAG, FUNCTION.APPLY_FILTER, FUNCTION.CLEAR_FILTER]

const TUTORIAL_BUTTONS = [FUNCTION.SAVE_MODEL, FUNCTION.FIT_TO_SCREEN, FUNCTION.RESET_LAYOUT, FUNCTION.INCREMENTAL_LAYOUT, FUNCTION.TOGGLE_VIEW_DIRECTION, FUNCTION.CUT, FUNCTION.COPY, FUNCTION.PASTE, FUNCTION.UNDO, FUNCTION.REDO, FUNCTION.SWITCH_TO_PAN_MODE, FUNCTION.SWITCH_TO_SELECT_MODE, FUNCTION.MANAGE_LABELS]

@Component({
    selector: 'app-toolbar-buttons',
    templateUrl: './toolbar-buttons.html',
    styleUrls: ['./toolbar-buttons.css'],
    standalone: false
})
export class ToolbarButtonsComponent implements OnInit, OnChanges{
    @Input() organisation:Organisation
    @Input() graphComponent:GraphComponent
    @Input() linqset: Linqset;
    @Input() dropdown:boolean
    @Input() screen:SCREEN
    @Input() selectedTagGroup: TagGroup
    @Input() readonlyGraph:boolean
    @Input() modelType:MODEL_TYPE  
    @Input() highlights:FUNCTION[]
    @Input() functions:FUNCTION[]   // which functions to show
    @Input() canCompare = false     // a project's model that has changed in it, so can be compared with current state
    @Input() comparing = false      // it is being compared just now

    @Output() toolbarEvent = new EventEmitter<{function:FUNCTION, value:any}>();

    FUNCTION = FUNCTION
    INSIGHTS_FIELD = INSIGHTS_FIELD
    LAYOUT_DIRECTION = LAYOUT_DIRECTION
    modelsForMove:ModelDetails[] = []
    modelTypes:MODEL_TYPE[]
    layouts:LAYOUTS[]

    currentModelType:MODEL_TYPE 

    selectedTagId:string
    EXPORT_TYPE = EXPORT_TYPE
    GROUP_TYPE = GROUP_TYPE
    NODE_TYPE = NODE_TYPE

    functionMap:any = {}

    constructor(private functionService:FunctionService, private graphService:LinqModelToCanvasService, private animation:ModelIntroductionService,
        private tagsService:TagsService, public app:AppComponent, private labelService:LabelService) {

    }

    // read from the graph, so the toolbar and its overflow menu agree, and a refused grouping (unsaved changes) isn't shown as grouped
    get isGrouped():boolean {
        return this.graphComponent?.graph?.nodes.some(n => n.tag?.group) || false
    }

    // the label shown on nodes, shared by the toolbar and its overflow menu
    get selectedLabel():INSIGHTS_FIELD {
        return this.labelService.nodeInsightsField
    }

    ngOnInit(): void {
        this.setup()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["linqset"] || changes["functions"]) this.setup()
        else if (changes["modelType"]) this.currentModelType = this.modelType
    }

    setup() {
        this.functionMap = this.functionService.getFunctionMap(false, this.linqset)
        this.modelsForMove = this.organisation?this.organisation.models.filter(m => !m.deletedDate && (m.role && m.role !== USER_ROLE.READ) && m._key !== this.linqset.modelKey):[]
        this.currentModelType = this.modelType
        this.modelTypes = this.graphService.getModelTypes(this.linqset)
        this.layouts = this.graphService.getLayouts(this.currentModelType)
    }

    disableButon(fn:FUNCTION) {
        return this.functionMap[fn].disabled
    }

    getBackups() {
        return this.linqset?.model?.backups?this.linqset.model.backups.filter(a => a.projectKey === this.linqset.workspaceKey):[]
    }

    getIcon(fn:FUNCTION) {
        return this.functionMap[fn].icon
    }

    isModelScreen() {
        return this.screen === SCREEN.MODEL
    }

    showButton(fn:FUNCTION) {
        // the temporal model is run from the analysis screen, not the model or insights screens
        if (fn === FUNCTION.TOGGLE_TEMPORAL_MODEL && (this.screen === SCREEN.MODEL || this.screen === SCREEN.INSIGHTS)) return false
        // AI builds empty models only: one with anything in it is changed by hand
        if (fn === FUNCTION.OPEN_MODELLING_AI && !this.isEmptyModel()) return false
        if (this.functions && this.functions.length > 0) return this.functions.indexOf(fn) >= 0       
        if (this.screen === SCREEN.INSIGHTS && (fn === FUNCTION.RECALCULATE_INSIGHTS || fn === FUNCTION.APPLY_FILTER || fn === FUNCTION.CLEAR_FILTER)) return false
        if (this.linqset.isTutorial()) return TUTORIAL_BUTTONS.indexOf(fn) >= 0
        
        if (this.isOrganisationLinqset() && ORG_EXCLUDED_FUNCTIONS.includes(fn))  {
            return false
        }
        if (this.readonlyGraph || isReadOnlyModelType(this.currentModelType)) return this.functionMap[fn]?.role === USER_ROLE.READ

        if (this.app.largeScreen) return this.dropdown?!LARGE_FUNCTIONS.includes(fn) && !MEDIUM_FUNCTIONS.includes(fn):LARGE_FUNCTIONS.includes(fn) || MEDIUM_FUNCTIONS.includes(fn)
        if (this.app.mediumScreen) return this.dropdown?!MEDIUM_FUNCTIONS.includes(fn):MEDIUM_FUNCTIONS.includes(fn)

        return true
    }

    // a process model with nothing in it yet: no actions, information, systems or roles of its own
    isEmptyModel() {
        const linqset = this.linqset
        if (!linqset || !linqset.isProcessModel() || linqset.isTutorial()) return false
        return !linqset.nodes.some(n => isModelNode(n.superType) ? n.modelKey === linqset.modelKey : true)
    }

    runAnimation() {
        this.animation.runAnimation(this.graphComponent, this.linqset)
    }
    
    buttonName(fn:FUNCTION) {
        return this.functionMap[fn].name
    }
    
    buttonDescription(fn:FUNCTION) {
        return this.functionMap[fn].description
    } 

    isHighlight(fn:FUNCTION) {
        return this.highlights && this.highlights.indexOf(fn) >= 0
    }

    readonly LAYOUTS = LAYOUTS

    // whether the model is lined up along this tag of the tag group shown
    isTagPath(tagKey:string) {
        return this.linqset?.layout === LAYOUTS.HIERARCHIC_TAG && this.linqset.layoutTag?.groupKey === this.selectedTagGroup?._key && this.linqset.layoutTag?.tagKey === tagKey
    }

    tagColour(tag:Tag) {
        return this.tagsService.getColourStyleForTagIndex(tag.tagIndex)
    }

    buttonClick(fn:Function, value?:any) {
        this.toolbarEvent.emit({function:fn._key, value:value})
        if (fn._key === FUNCTION.CHANGE_MODEL_TYPE) this.currentModelType = value
    }  

    isDisabled(fn:FUNCTION) {
        return this.functionMap[fn].disabled
    }

    // the project has its own version of this model to replace current state with: it changed the model, or made it
    canReplaceCurrentState() {
        if (!this.linqset || this.linqset.isCurrentState()) return false
        return this.linqset.changedByProject() || this.linqset.model.projectKey === this.linqset.workspaceKey
    }

    isUnsavedChanges() {
        return this.linqset && this.linqset.isDirty()
    }

    // backups for this workspace, as those are the ones the restore menu lists
    noBackups() {
        return this.getBackups().length === 0
    }

    isOrganisationLinqset() {
        if (!this.linqset) return false
        return this.linqset.isOrganisationModel()
    }

    getTagGroups() {
        return this.organisation.tagGroups
    }

    getTags() {
        return this.selectedTagGroup?this.selectedTagGroup.tags:[]
    }

    getLabelFieldTypes() {
        return Object.values(INSIGHTS_FIELD)
    }

    getTagIcon(tag:Tag) {
        return this.tagsService.getIconForTag(tag)
    }
}
